import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, TextField, Typography, Box, Radio, RadioGroup, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getInputFieldsConfig } from '../config';
import { t } from '../config';

// ── Date formatting helpers (shared with input-fields-panel) ─────────
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatDate(date, format) {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = String(date.getFullYear());
  const monthName = MONTH_NAMES[date.getMonth()];

  switch (format) {
    case 'MM-DD-YYYY':
      return `${mm}-${dd}-${yyyy}`;
    case 'MMMM-DD-YYYY':
      return `${monthName}-${dd}-${yyyy}`;
    case 'MMMM-DD':
      return `${monthName}-${dd}`;
    case 'YYYY':
      return yyyy;
    case 'MM-DD':
      return `${mm}-${dd}`;
    default:
      return `${mm}-${dd}-${yyyy}`;
  }
}

function parseDateFromFormat(dateStr, format) {
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d;
  } catch (e) {
    // ignore
  }
  return new Date();
}

// ── Default single-field dialog (full-page overlay) ──────────────────
const SingleFieldDialog = ({ field, onSubmit, onSkip, onBack, canGoBack, currentIndex, totalCount }) => {
  const [value, setValue] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const config = getInputFieldsConfig();

  const custom = field.custom || {};
  const inputType = custom.inputType || 'text';
  const promptText = custom.promptText || custom.placeholder || 'Please provide input';
  const dateFormat = custom.dateFormat || 'MM-DD-YYYY';
  const isRequired = custom.required === true;
  const options = custom.options || [];
  const minSelections = custom.minSelections || 0;
  const maxSelections = custom.maxSelections || options.length;
  const isSubmitDisabled = inputType === 'list'
    ? selectedItems.length < minSelections
    : isRequired && !value;
  const logoUrl = config.logoUrl || null;
  const headerText = config.headerText || null;
  const accentColor = config.accentColor || '#0d83cd';
  const progressPct = Math.round(((currentIndex + 1) / totalCount) * 100);

  useEffect(() => {
    setValue('');
    setImagePreview(null);
    setSelectedItems([]);
  }, [field.id]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataURL = ev.target.result;
      setValue(dataURL);
      setImagePreview(dataURL);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = () => {
    if (inputType === 'date' && value) {
      const dateObj = parseDateFromFormat(value, dateFormat);
      const formatted = formatDate(dateObj, dateFormat);
      onSubmit(field.id, formatted);
    } else if (inputType === 'list') {
      onSubmit(field.id, selectedItems.join(', '));
    } else {
      onSubmit(field.id, value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputType !== 'image' && inputType !== 'list' && inputType !== 'radio') handleSubmit();
  };

  const renderInput = () => {
    const sharedSx = {
      mt: 1,
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        fontSize: 22,
        '& fieldset': { borderColor: '#d0d5dd' },
        '&:hover fieldset': { borderColor: accentColor },
        '&.Mui-focused fieldset': { borderColor: accentColor },
      },
      ...config.inputStyle,
    };

    switch (inputType) {
      case 'date':
        return (
          <TextField
            fullWidth
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            InputLabelProps={{ shrink: true }}
            sx={sharedSx}
          />
        );
      case 'integer':
        return (
          <TextField
            fullWidth
            type="number"
            value={value}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || /^-?\d*$/.test(v)) setValue(v);
            }}
            onKeyDown={handleKeyDown}
            autoFocus
            placeholder="0"
            inputProps={{ step: 1 }}
            sx={sharedSx}
          />
        );
      case 'image':
        return (
          <Box sx={{ mt: 1 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
            {imagePreview ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: '100%',
                    maxHeight: 260,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain' }}
                  />
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: 'none', fontSize: 13, borderRadius: '8px', borderColor: '#d0d5dd' }}
                >
                  {t('inputFieldsDialog.changeImage', 'Change Image')}
                </Button>
              </Box>
            ) : (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: '2px dashed #d0d5dd',
                  borderRadius: '10px',
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': { borderColor: accentColor, backgroundColor: 'rgba(13,131,205,0.03)' },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 40, color: '#bbb', mb: 1.5 }} />
                <Typography variant="body2" sx={{ color: '#888', fontSize: 14 }}>
                  {t('inputFieldsDialog.uploadImage', 'Click to select an image')}
                </Typography>
              </Box>
            )}
          </Box>
        );
      case 'radio':
        return (
          <RadioGroup
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{ mt: 1, gap: 1 }}
          >
            {options.map((opt) => (
              <FormControlLabel
                key={opt}
                value={opt}
                control={
                  <Radio sx={{ color: '#d0d5dd', '&.Mui-checked': { color: accentColor } }} />
                }
                label={<Typography sx={{ fontSize: 18 }}>{opt}</Typography>}
                sx={{
                  m: 0,
                  border: '1px solid',
                  borderColor: value === opt ? accentColor : '#d0d5dd',
                  borderRadius: '8px',
                  px: 2,
                  py: 0.75,
                  transition: 'all 0.15s ease',
                  backgroundColor: value === opt ? 'rgba(13,131,205,0.04)' : 'transparent',
                  '&:hover': { borderColor: accentColor, backgroundColor: 'rgba(13,131,205,0.03)' },
                }}
              />
            ))}
          </RadioGroup>
        );
      case 'list': {
        const isAtMax = maxSelections > 0 && selectedItems.length >= maxSelections;
        return (
          <Box sx={{ mt: 1 }}>
            <FormGroup sx={{ gap: 1 }}>
              {options.map((opt) => {
                const checked = selectedItems.includes(opt);
                const disabled = !checked && isAtMax;
                return (
                  <FormControlLabel
                    key={opt}
                    control={
                      <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems((prev) => [...prev, opt]);
                          } else {
                            setSelectedItems((prev) => prev.filter((i) => i !== opt));
                          }
                        }}
                        sx={{ color: '#d0d5dd', '&.Mui-checked': { color: accentColor } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 18, opacity: disabled ? 0.45 : 1 }}>{opt}</Typography>
                    }
                    sx={{
                      m: 0,
                      border: '1px solid',
                      borderColor: checked ? accentColor : '#d0d5dd',
                      borderRadius: '8px',
                      px: 2,
                      py: 0.75,
                      opacity: disabled ? 0.6 : 1,
                      transition: 'all 0.15s ease',
                      backgroundColor: checked ? 'rgba(13,131,205,0.04)' : 'transparent',
                    }}
                  />
                );
              })}
            </FormGroup>
            <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1.5 }}>
              {selectedItems.length}{maxSelections > 0 ? `/${maxSelections}` : ''} selected
              {minSelections > 0 && ` · min ${minSelections} required`}
            </Typography>
          </Box>
        );
      }
      case 'text':
      default:
        return (
          <TextField
            fullWidth
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            placeholder={custom.placeholder || ''}
            sx={sharedSx}
          />
        );
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: '#f7f8fa',
        display: 'flex',
        flexDirection: 'column',
        ...config.dialogStyle,
      }}
    >
      {/* Progress bar */}
      <Box sx={{ height: 4, backgroundColor: '#e4e7ec', flexShrink: 0 }}>
        <Box
          sx={{
            height: '100%',
            width: `${progressPct}%`,
            backgroundColor: accentColor,
            transition: 'width 0.4s ease',
          }}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 4,
          py: 1.75,
          flexShrink: 0,
          ...config.titleStyle,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo"
              style={{ maxHeight: 36, maxWidth: 200, objectFit: 'contain' }}
            />
          )}
          {(headerText || !logoUrl) && (
            <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#1a1a1a', letterSpacing: '-0.2px' }}>
              {headerText || 'LabelLife'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Centered content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 5 },
          overflowY: 'auto',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 750 }}>
          {/* Step indicator */}
          <Typography
            sx={{ display: 'block', fontSize: 18, color: '#999', fontWeight: 500, mb: 1.5, letterSpacing: '0.2px' }}
          >
            {t('inputFieldsDialog.stepIndicator', 'Field {current} of {total}')
              .replace('{current}', currentIndex + 1)
              .replace('{total}', totalCount)}
          </Typography>

          {/* Main question */}
          <Typography
            sx={{
              fontSize: { xs: 33, sm: 39 },
              fontWeight: 700,
              color: '#111827',
              lineHeight: 1.3,
              mb: 4.5,
              ...config.promptStyle,
            }}
          >
            {promptText}
          </Typography>

          {/* Input */}
          {renderInput()}

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4.5 }}>
            {canGoBack && (
              <Button
                variant="outlined"
                onClick={onBack}
                startIcon={<ArrowBackIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: 21,
                  px: 3,
                  py: 1.65,
                  borderRadius: '10px',
                  color: '#555',
                  borderColor: '#d0d5dd',
                  '&:hover': { borderColor: '#aaa', backgroundColor: 'rgba(0,0,0,0.02)' },
                }}
              >
                {t('inputFieldsDialog.back', 'Back')}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 21,
                px: 5,
                py: 1.65,
                borderRadius: '10px',
                backgroundColor: accentColor,
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#0a6eb3', boxShadow: 'none' },
                ...config.submitButtonStyle,
              }}
            >
              {config.submitButtonText || t('inputFieldsDialog.submit', 'Continue')}
            </Button>
            {(inputType === 'list' ? minSelections === 0 : !isRequired) && (
              <Button
                variant="outlined"
                onClick={() => onSkip(field.id)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: 21,
                  px: 4.5,
                  py: 1.65,
                  borderRadius: '10px',
                  color: '#555',
                  borderColor: '#d0d5dd',
                  '&:hover': { borderColor: '#aaa', backgroundColor: 'rgba(0,0,0,0.02)' },
                  ...config.skipButtonStyle,
                }}
              >
                {config.skipButtonText || t('inputFieldsDialog.skip', 'Do it later')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Main orchestrator component ──────────────────────────────────────
const InputFieldsDialog = observer(({ store, onComplete, enabled = true, backTrigger = 0 }) => {
  // displayIndex counts how many fields have been answered (for step indicator only).
  // Navigation always uses fields[0] — the first remaining pending field.
  const [displayIndex, setDisplayIndex] = useState(0);
  const initialTotalRef = useRef(0);
  const completedHistoryRef = useRef([]);
  const isGoingBackRef = useRef(false);
  const prevBackTriggerRef = useRef(backTrigger);
  const config = getInputFieldsConfig();

  const pendingFields = store._pendingInputFields || [];

  // When disabled, clear any pending fields so they are treated as skipped
  useEffect(() => {
    if (!enabled && pendingFields.length > 0) {
      store.clearPendingInputFields();
    }
  }, [enabled, pendingFields.length, store]);

  const fields = pendingFields.filter(
    (f) => f.custom && f.custom.inputField
  );

  // Capture total and reset display counter when a fresh batch appears
  useEffect(() => {
    if (fields.length > 0 && initialTotalRef.current === 0) {
      if (isGoingBackRef.current) {
        // Back-from-summary restored a field — recover total from history + pending
        isGoingBackRef.current = false;
        initialTotalRef.current = completedHistoryRef.current.length + fields.length;
      } else {
        // Genuine fresh batch
        initialTotalRef.current = fields.length;
        setDisplayIndex(0);
        completedHistoryRef.current = [];
      }
    }
    if (fields.length === 0) {
      initialTotalRef.current = 0;
      // History is intentionally kept so back-from-summary can use it
    }
  }, [fields.length]);

  const totalCount = initialTotalRef.current || fields.length;

  // Back-from-summary: triggered by parent incrementing backTrigger prop
  // Must be defined and its useEffect called BEFORE any early returns (Rules of Hooks)
  const handleBackFromSummary = () => {
    const last = completedHistoryRef.current.pop();
    if (!last) return;
    const { el, previousValue } = last;
    if (el) {
      const isImage = el.custom && el.custom.inputType === 'image';
      el.set(isImage ? { src: previousValue || '' } : { text: previousValue || '' });
      isGoingBackRef.current = true;
      store._unresolveInputField(el);
    }
    // displayIndex stays unchanged — it already points to the last step (N-1)
  };

  useEffect(() => {
    if (backTrigger !== prevBackTriggerRef.current) {
      prevBackTriggerRef.current = backTrigger;
      handleBackFromSummary();
    }
  }, [backTrigger]);

  if (!enabled || fields.length === 0) return null;

  // Always show the first remaining field — the array shrinks as fields are resolved
  const currentField = fields[0];
  if (!currentField) return null;

  // ── Shared submit / skip / back helpers ─────────────────────────────
  const handleSubmit = (elementId, value) => {
    const el = store.getElementById(elementId);
    const isImage = el && el.custom && el.custom.inputType === 'image';
    const previousValue = el ? (isImage ? el.src : el.text) : '';
    completedHistoryRef.current.push({ el, previousValue, skipped: false });
    if (el && el.set) {
      if (isImage) {
        el.set({ src: String(value) });
      } else {
        el.set({ text: String(value) });
      }
    }
    store._resolveInputField(elementId);
    if (fields.length <= 1) {
      store.history.addUndoState();
      if (onComplete) onComplete();
    } else {
      setDisplayIndex((prev) => prev + 1);
    }
  };

  const handleSkip = (elementId) => {
    const el = store.getElementById(elementId);
    const isImage = el && el.custom && el.custom.inputType === 'image';
    const previousValue = el ? (isImage ? el.src : el.text) : '';
    completedHistoryRef.current.push({ el, previousValue, skipped: true });
    store._resolveInputField(elementId);
    if (fields.length <= 1) {
      if (onComplete) onComplete();
    } else {
      setDisplayIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    const last = completedHistoryRef.current.pop();
    if (!last) return;
    const { el, previousValue } = last;
    if (el) {
      const isImage = el.custom && el.custom.inputType === 'image';
      el.set(isImage ? { src: previousValue || '' } : { text: previousValue || '' });
      store._unresolveInputField(el);
    }
    setDisplayIndex((prev) => Math.max(0, prev - 1));
  };

  const canGoBack = displayIndex > 0;

  // ── Resolve which dialog component to use ───────────────────────────
  // Priority: per-type custom dialog > global CustomDialog > built-in
  const TYPE_DIALOG_MAP = {
    text: config.CustomTextDialog,
    date: config.CustomDateDialog,
    integer: config.CustomIntegerDialog,
    image: config.CustomImageDialog,
    radio: config.CustomRadioDialog,
    list: config.CustomListDialog,
  };

  const fieldType = (currentField.custom && currentField.custom.inputType) || 'text';
  const PerTypeDialog = TYPE_DIALOG_MAP[fieldType];
  const GlobalCustomDialog = config.CustomDialog;
  const ResolvedDialog = PerTypeDialog || GlobalCustomDialog || null;

  if (ResolvedDialog) {
    return (
      <ResolvedDialog
        field={currentField}
        fields={fields}
        currentIndex={displayIndex}
        totalCount={totalCount}
        onSubmit={handleSubmit}
        onSkip={handleSkip}
        onBack={handleBack}
        canGoBack={canGoBack}
        onComplete={() => {
          store.history.addUndoState();
          if (onComplete) onComplete();
        }}
      />
    );
  }

  return (
    <SingleFieldDialog
      field={currentField}
      onSubmit={handleSubmit}
      onSkip={handleSkip}
      onBack={handleBack}
      canGoBack={canGoBack}
      currentIndex={displayIndex}
      totalCount={totalCount}
    />
  );
});

InputFieldsDialog.displayName = 'InputFieldsDialog';
export default InputFieldsDialog;
export { InputFieldsDialog };
